import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import { toast } from "react-toastify";
import { useCart } from "@/contexts/CartContext";
import { routes } from "@/consts";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  // Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Calendar, FileText, Save, X, Plus } from "lucide-react";
import { LANG } from "@/types";
import useTender from "@/hooks/useTender";
import { useShipping } from "@/hooks/use-shipping";
import regionsData from "@/app/features/company/data/regions_lite.json";
import citiesData from "@/app/features/company/data/cities_lite.json";

interface Address {
  id: string;
  fullName: string;
  address: string;
  region: string;
  city: string;
  phoneNumber: string;
  isDefault: boolean;
}

interface Region {
  region_id: number;
  name_ar: string;
}

interface City {
  city_id: number;
  region_id: number;
  name_ar: string;
}

interface Option {
  value: string;
  label: string;
}

// Schema for the inquiry form
const createInquirySchema = (t: (key: string) => string) =>
  z
    .object({
      endDate: z.string().min(1, t("tender.validation.endDateRequired")),
      title: z.string().min(1, t("tender.validation.titleRequired")),
      openingDate: z
        .string()
        .min(1, t("tender.validation.deliverDateRequired1")),
      fileId: z
        .union([z.instanceof(File), z.string()])
        .nullable()
        .optional(),
    })
    .refine(
      (data) => {
        const endDate = new Date(data.endDate);
        const openingDate = new Date(data.openingDate);
        return (
          !isNaN(endDate.getTime()) &&
          !isNaN(openingDate.getTime()) &&
          openingDate > endDate
        );
      },
      {
        message: t("tender.validation.deliverDateAfterEndDate"),
        path: ["deliverDate"],
      }
    );

type InquiryFormData = z.infer<ReturnType<typeof createInquirySchema>>;

export default function InquiryFormPage() {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const navigate = useNavigate();
  const { inquiryItems, removeFromInquiry, updateInquiryItem, clearInquiry } =
    useCart();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<string>("");
  const [addresses, setAddresses] = useState<any[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newAddress, setNewAddress] = useState<Partial<Address>>({
    fullName: "",
    address: "",
    region: "",
    city: "",
    phoneNumber: "",
    isDefault: false,
  });
  const [cities, setCities] = useState<Option[]>([]);

  const { myShipping, createShipping } = useShipping();
  const { createOffer } = useTender();

  const InquirySchema = createInquirySchema(t as (key: string) => string);

  const form = useForm<InquiryFormData>({
    resolver: zodResolver(InquirySchema),
    defaultValues: {
      title: "",
      endDate: "",
      fileId: null,
      openingDate: "",
    },
  });

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const data = await myShipping();
      if (data) {
        setAddresses(data);
        const defaultAddress = data.find((addr: any) => addr.isDefault);
        if (defaultAddress) {
          setSelectedAddress(defaultAddress.id);
        }
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  // Update cities when region changes
  useEffect(() => {
    const selectedRegion = newAddress.region;
    if (selectedRegion) {
      const filteredCities = citiesData
        .filter((city: City) => city.region_id === parseInt(selectedRegion))
        .map((city: City) => ({
          value: city?.city_id.toString(),
          label: city.name_ar,
        }));
      setCities(filteredCities);
    } else {
      setCities([]);
    }
  }, [newAddress.region]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      // Set the first file as the fileId for the form
      form.setValue("fileId", files[0] || null);
    }
  };

  const handleProductChange = (
    itemId: string,
    field: string,
    value: string | number
  ) => {
    updateInquiryItem(itemId, { [field]: value });
  };

  const handleRemoveProduct = (itemId: string) => {
    removeFromInquiry(itemId);
  };

  const handleAddAddress = async () => {
    try {
      const response = await createShipping({
        fullName: newAddress.fullName || "",
        address: newAddress.address || "",
        region: newAddress.region || "",
        phoneNumber: newAddress.phoneNumber || "",
        city: newAddress.city || "",
        isDefault: newAddress.isDefault || false,
      });

      if (response) {
        await fetchAddresses();
        setIsAddDialogOpen(false);
        setNewAddress({
          fullName: "",
          address: "",
          region: "",
          city: "",
          phoneNumber: "",
          isDefault: false,
        });
      }
    } catch (error) {
      console.error("Error adding address:", error);
    }
  };

  const onSubmit = async (values: InquiryFormData) => {
    if (inquiryItems.length === 0) {
      toast.error("Please add at least one product to inquiry");
      return;
    }

    setIsSubmitting(true);
    try {
      const products = inquiryItems.map((item) => ({
        item: item.name,
        quantity: item.quantity,
        unit: item.unit,
        note: item.note || "",
      }));

      // Get unique categories from all inquiry items
      const allCategories = inquiryItems
        .filter((item) => item.categories[0].length > 0)
        .flatMap((item) => item.categories || []);
      const uniqueCategories = [...new Set(allCategories)];

      let payload: any = {
        ...values,
        title: values.title,
        openingDate: values.openingDate,
        endDate: values.endDate,
        products: products,
        categoriesIds: uniqueCategories,
        type: "Huge",
        addressId: selectedAddress,
      };

      const response = await createOffer(payload);
      if (response) {
        clearInquiry();
        navigate(routes.tenders);
      }
    } catch (error) {
      console.error("Error creating quote:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const regions = regionsData.map((region: Region) => ({
    value: region.region_id.toString(),
    label: region.name_ar,
  }));

  // Redirect if no inquiry items
  if (inquiryItems.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Box sx={{ maxWidth: "4xl", mx: "auto", textAlign: "center" }}>
          <Typography
            variant="h4"
            sx={{ mb: 3, fontWeight: 600, color: "#374151" }}
          >
            {t("actions.addTenderRequest")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: "#6b7280" }}>
            No products in your inquiry. Please add products from the
            marketplace first.
          </Typography>
          <Button
            onClick={() => navigate(routes.marketPlace)}
            variant="contained"
            sx={{
              background: "#18181b",
              color: "white",
              borderRadius: 2,
              px: 3,
              py: 1.5,
            }}
          >
            {t("navigation.goToMarketplace")}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ maxWidth: "4xl", mx: "auto" }}>
        <Typography
          variant="h4"
          sx={{ mb: 4, fontWeight: 600, color: "#374151" }}
        >
          {t("actions.addTenderRequest")}
        </Typography>

        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "none",
                  border: "1px solid #e5e7eb",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 3, fontWeight: 600, color: "#374151" }}
                  >
                    {t("general.purchaseRequest")} {t("general.information")}
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={
                          t("orders.placeholders.enterRequestTitle") + " *"
                        }
                        {...form.register("title")}
                        error={!!form.formState.errors.title}
                        helperText={form.formState.errors.title?.message}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        inputProps={{
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="file"
                        onChange={handleFileChange}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        inputProps={
                          {
                            dir: locale === LANG.AR ? "rtl" : "ltr",
                            multiple: true,
                            accept: "*/*",
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                        InputProps={{
                          startAdornment:
                            locale === LANG.EN ? (
                              <FileText
                                size={16}
                                style={{ marginRight: 8, color: "#6b7280" }}
                              />
                            ) : null,
                          endAdornment:
                            locale === LANG.AR ? (
                              <FileText
                                size={16}
                                style={{ marginLeft: 8, color: "#6b7280" }}
                              />
                            ) : null,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            paddingRight: "12px",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label={t("offers.fields.submissionDeadline")}
                        {...form.register("endDate")}
                        error={!!form.formState.errors.endDate}
                        helperText={form.formState.errors.endDate?.message}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        inputProps={{
                          min: new Date(
                            new Date().setDate(new Date().getDate() + 1)
                          )
                            .toISOString()
                            .split("T")[0],
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        InputProps={{
                          startAdornment:
                            locale === LANG.EN ? (
                              <Calendar
                                size={16}
                                style={{ marginRight: 8, color: "#6b7280" }}
                              />
                            ) : null,
                          endAdornment:
                            locale === LANG.AR ? (
                              <Calendar
                                size={16}
                                style={{ marginLeft: 8, color: "#6b7280" }}
                              />
                            ) : null,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            paddingRight: "12px",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            "&[data-shrink='false']": {
                              pr: locale === LANG.AR ? "20px" : "0px",
                            },
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label={t("offers.fields.openingDate")}
                        {...form.register("openingDate")}
                        error={!!form.formState.errors.openingDate}
                        helperText={form.formState.errors.openingDate?.message}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        inputProps={{
                          min: (() => {
                            const endDate = form.watch("endDate");
                            if (!endDate) return "";

                            try {
                              const date = new Date(endDate);
                              if (isNaN(date.getTime())) return "";

                              date.setDate(date.getDate() + 1);
                              return date.toISOString().split("T")[0];
                            } catch (error) {
                              return "";
                            }
                          })(),
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        InputProps={{
                          startAdornment:
                            locale === LANG.EN ? (
                              <Calendar
                                size={16}
                                style={{ marginRight: 8, color: "#6b7280" }}
                              />
                            ) : null,
                          endAdornment:
                            locale === LANG.AR ? (
                              <Calendar
                                size={16}
                                style={{ marginLeft: 8, color: "#6b7280" }}
                              />
                            ) : null,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            paddingRight: "12px",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            "&[data-shrink='false']": {
                              pr: locale === LANG.AR ? "20px" : "0px",
                            },
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      {addresses.length > 0 ? (
                        <FormControl fullWidth>
                          <InputLabel
                            dir={locale === LANG.AR ? "rtl" : "ltr"}
                            sx={{
                              transformOrigin:
                                locale === LANG.AR ? "right" : "left",
                              direction: locale === LANG.AR ? "rtl" : "ltr",
                              ...(locale === LANG.AR
                                ? { right: 30, left: "auto" }
                                : { left: 0, right: "auto" }),
                            }}
                          >
                            {t("checkout.fields.shippingAddress")}
                          </InputLabel>
                          <Select
                            value={selectedAddress}
                            onChange={(e) => setSelectedAddress(e.target.value)}
                            label={t("checkout.fields.shippingAddress")}
                            dir={locale === LANG.AR ? "rtl" : "ltr"}
                            sx={{
                              borderRadius: 2,
                              direction: locale === LANG.AR ? "rtl" : "ltr",
                              "& .MuiSelect-select": {
                                paddingRight: "10px !important",
                              },
                              "& .MuiSelect-icon": {
                                left: locale === LANG.EN ? "unset" : 0,
                                marginLeft: 0,
                                right: locale === LANG.EN ? 0 : "unset",
                              },
                              "&:hover": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#644980",
                                },
                              },
                              "&.Mui-focused": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#644980",
                                  borderWidth: 2,
                                },
                              },
                              "& legend": {
                                textAlign: locale === LANG.AR ? "end" : "start",
                              },
                            }}
                          >
                            {addresses.map((address) => (
                              <MenuItem key={address.id} value={address.id}>
                                {address.fullName} - {address.address}
                  </MenuItem>
                ))}
              </Select>
                        </FormControl>
                      ) : (
                        <Button
                          variant="outlined"
                          startIcon={
                            locale === LANG.EN ? <Plus size={16} /> : null
                          }
                          endIcon={
                            locale === LANG.AR ? <Plus size={16} /> : null
                          }
                          onClick={() => setIsAddDialogOpen(true)}
                          fullWidth
                          dir={locale === LANG.AR ? "rtl" : "ltr"}
                          sx={{
                            borderColor: "#d1d5db",
                            color: "#6b7280",
                            borderRadius: 2,
                            py: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            "&:hover": {
                              borderColor: "#644980",
                              color: "#644980",
                            },
                          }}
                        >
                          {t("checkout.fields.shippingAddress")}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Products Section */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "none",
                  border: "1px solid #e5e7eb",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 3, fontWeight: 600, color: "#374151" }}
                  >
                    {t("products.list")}
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {inquiryItems.map((item) => (
                      <Card
                        key={item.id}
                        sx={{
                          borderRadius: 2,
                          border: "1px solid #f3f4f6",
                          boxShadow: "none",
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Box
                                component="img"
                            src={item?.images?.[0]}
                            alt={item.name}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 2,
                                  objectFit: "cover",
                                }}
                              />
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, color: "#374151" }}
                              >
                                {item.name}
                              </Typography>
                            </Box>
                            <IconButton
                            onClick={() => handleRemoveProduct(item.id)}
                              sx={{
                                color: "#ef4444",
                                "&:hover": {
                                  backgroundColor: "#fef2f2",
                                },
                              }}
                            >
                              <X size={20} />
                            </IconButton>
                          </Box>

                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label={t("checkout.fields.quantity")}
                            type="number"
                            value={item.quantity}
                            onChange={(e) =>
                              handleProductChange(
                                item.id,
                                "quantity",
                                Number(e.target.value)
                              )
                            }
                                inputProps={{ min: 1 }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    "&:hover": {
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#644980",
                                      },
                                    },
                                    "&.Mui-focused": {
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#644980",
                                        borderWidth: 2,
                                      },
                                    },
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label={t("orders.fields.notes")}
                            value={item.note || ""}
                            onChange={(e) =>
                              handleProductChange(
                                item.id,
                                "note",
                                e.target.value
                              )
                            }
                                multiline
                                rows={2}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    "&:hover": {
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#644980",
                                      },
                                    },
                                    "&.Mui-focused": {
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#644980",
                                        borderWidth: 2,
                                      },
                                    },
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                    </CardContent>
                  </Card>
            </Grid>
          </Grid>

          {/* Submit Button */}
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderColor: "#d1d5db",
                color: "#6b7280",
                borderRadius: 2,
                px: 3,
                py: 1.5,
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              {t("actions.cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={<Save size={16} />}
              sx={{
                background: "#18181b",
                color: "white",
                borderRadius: 2,
                px: 3,
                py: 1.5,
                "&:disabled": {
                  background: "#9ca3af",
                },
              }}
            >
              {isSubmitting ? t("actions.submitting") : t("actions.submit")}
            </Button>
          </Box>
        </form>

        {/* Add Address Dialog */}
        <Dialog
          dir={locale === LANG.AR ? "rtl" : "ltr"}
          open={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: "90vh",
              m: 2,
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
              borderBottom: "1px solid #e5e7eb",
              p: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#374151" }}
              >
                {t("delivery.addNewAddress")}
              </Typography>
              <IconButton
                onClick={() => setIsAddDialogOpen(false)}
                sx={{
                  color: "#6b7280",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                  },
                }}
              >
                <X size={20} />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("profile.fields.nickName")}
                  value={newAddress.fullName}
                  onChange={(e) =>
                    setNewAddress({ ...newAddress, fullName: e.target.value })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                          borderWidth: 2,
                        },
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("company.fields.address")}
                  value={newAddress.address}
                  onChange={(e) =>
                    setNewAddress({ ...newAddress, address: e.target.value })
                  }
                  multiline
                  rows={2}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                          borderWidth: 2,
                        },
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("company.fields.region")}</InputLabel>
                  <Select
                    value={newAddress.region}
                    label={t("company.fields.region")}
                    onChange={(e) =>
                      setNewAddress({ ...newAddress, region: e.target.value })
                    }
                    sx={{
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                          borderWidth: 2,
                        },
                      },
                    }}
                  >
                    {regions.map((region) => (
                      <MenuItem key={region.value} value={region.value}>
                        {region.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("company.fields.city")}</InputLabel>
                  <Select
                    value={newAddress.city}
                    label={t("company.fields.city")}
                    onChange={(e) =>
                      setNewAddress({ ...newAddress, city: e.target.value })
                    }
                    sx={{
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                          borderWidth: 2,
                        },
                      },
                    }}
                  >
                    {cities.map((city) => (
                      <MenuItem key={city.value} value={city.value}>
                        {city.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("profile.fields.phoneNumber")}
                  value={newAddress.phoneNumber}
                  onChange={(e) =>
                    setNewAddress({
                      ...newAddress,
                      phoneNumber: e.target.value,
                    })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                          borderWidth: 2,
                        },
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
              borderTop: "1px solid #e5e7eb",
              gap: 2,
            }}
          >
            <Button
              onClick={() => setIsAddDialogOpen(false)}
              variant="outlined"
              sx={{
                borderColor: "#d1d5db",
                color: "#6b7280",
                borderRadius: 2,
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              {t("actions.cancel")}
            </Button>
            <Button
              onClick={handleAddAddress}
              variant="contained"
              startIcon={<Save size={16} />}
              sx={{
                background: "linear-gradient(135deg, #644980 0%, #1d4ed8 100%)",
                color: "white",
                borderRadius: 2,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #644980 0%, #1e40af 100%)",
                },
              }}
            >
              {t("actions.save")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
